import React, { useState, useEffect } from 'react';

const API_URL = '/api';

const AuthScreen = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [recoveryKeyword, setRecoveryKeyword] = useState('');
    const [isRecoveryMode, setIsRecoveryMode] = useState(false);

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // ========================================================================
    // FAXINA PESADA SILENCIOSA
    // ========================================================================
    useEffect(() => {
        sessionStorage.clear();
        localStorage.clear();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(''); setSuccessMessage(''); setLoading(true);
        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ username, password }),
            });

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                if (!response.ok) {
                    if (data.error === 'MONITORAMENTO_REQUERIDO') throw new Error(data.message);
                    throw new Error(data.error || `Erro ${response.status}`);
                }
                onLogin(data.accessToken);
            } else {
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Erro ${response.status}: ${errorText || response.statusText}`);
                }
            }
        } catch (err) {
            setError(`Falha no login: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRecovery = async (e) => {
        e.preventDefault();
        setError(''); setSuccessMessage(''); setLoading(true);
        try {
            const response = await fetch(`${API_URL}/recover-password`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ username, recoveryKeyword }),
            });

            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                if (!response.ok) throw new Error(data.error || 'Erro na recuperação.');
                
                setSuccessMessage(`${data.message}\nSenha: ${data.tempPassword}`);
                setIsRecoveryMode(false);
            } else {
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Erro ${response.status}: ${errorText || response.statusText}`);
                }
            }
        } catch (err) {
            setError(`Falha na recuperação: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleRecoveryMode = () => {
        setIsRecoveryMode(!isRecoveryMode);
        setError(''); setSuccessMessage('');
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 font-inter">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg">
                <div className="text-center">
                    <img src="/logo_dfinai.png" alt="DFinAI Logo" className="w-62 h-32 mx-auto mb-1" />
                    <h2 className="text-3xl font-bold text-gray-900">Bem-vindo!</h2>
                    <h3 className="mt-2 text-gray-600">{isRecoveryMode ? 'Recupere a sua senha' : 'Faça login para continuar'}</h3>
                </div>

                <form className="mt-8 space-y-6" onSubmit={isRecoveryMode ? handleRecovery : handleLogin}>
                    {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-lg border border-red-200">{error}</p>}
                    {successMessage && <p className="p-3 text-sm text-green-700 bg-green-100 rounded-lg whitespace-pre-wrap">{successMessage}</p>}

                    <div className="relative">
                        <input id="username" name="username" type="text" required value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 text-gray-900 placeholder-transparent bg-gray-100 border-2 border-gray-200 rounded-lg peer focus:outline-none focus:border-blue-500" placeholder="Nome de Utilizador" />
                        <label htmlFor="username" className="absolute left-4 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm">Nome de Utilizador</label>
                    </div>

                    {isRecoveryMode ? (
                        <div className="relative">
                            <input id="recoveryKeyword" name="recoveryKeyword" type="text" required value={recoveryKeyword} onChange={(e) => setRecoveryKeyword(e.target.value)} className="w-full px-4 py-3 text-gray-900 placeholder-transparent bg-gray-100 border-2 border-gray-200 rounded-lg peer focus:outline-none focus:border-blue-500" placeholder="Palavra-Chave" />
                            <label htmlFor="recoveryKeyword" className="absolute left-4 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm">Palavra-Chave de Recuperação</label>
                        </div>
                    ) : (
                         <div className="relative">
                            <input id="password" name="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 text-gray-900 placeholder-transparent bg-gray-100 border-2 border-gray-200 rounded-lg peer focus:outline-none focus:border-blue-500" placeholder="Senha" />
                             <label htmlFor="password" className="absolute left-4 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm">Senha</label>
                        </div>
                    )}

                    <div>
                        <button type="submit" disabled={loading} className="w-full px-4 py-3 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300">
                            {loading ? 'A processar...' : (isRecoveryMode ? 'Recuperar' : 'Entrar')}
                        </button>
                    </div>
                </form>

                <p className="text-sm text-center text-gray-600">
                    <button onClick={toggleRecoveryMode} className="font-medium text-blue-600 hover:text-blue-500">
                        {isRecoveryMode ? 'Voltar para o Login' : 'Esqueceu a sua senha?'}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default AuthScreen;